import { FC, useEffect, useState } from "react";
import { Card, CardBody, Col, Form, FormGroup, Label, Row } from "reactstrap";
import ModalComponent from "../../shared/base-components/Modal";
import TextField from "../../shared/base-components/TextField";
import { SubmitHandler, useForm } from "react-hook-form";
import Button from "../../shared/base-components/Button";
import SelectBox from "../../shared/base-components/SelectBox";
import DatePicker from "../../shared/base-components/DatePicker";
import {
  useCustomerLastMaintenanceMutation,
  useCustomerMaintenanceMutation,
} from "./CustomerProfile.actions";
import { getItem } from "../../shared/hooks/useCookie";
import {
  CustomerLastMaintenanceResponse,
  CustomerProfileResponse,
  OperationTypes,
  customerMaintenanceSchema,
  getOperationTypes,
} from "./CustomerProfile.constants";
import { Link } from "react-router-dom";
import { useDevicePartListMutation } from "../CustomerAdd/CustomerAdd.actions";
import { AxiosError } from "axios";
import { UseMutateFunction } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { useStaffListMutation } from "../Staff/Staff.actions";
import { useRecoilValue } from "recoil";
import { isLoggedInSelector } from "../../shared/actions/Authentication.actions";

interface CustomerMaintenanceModalProps {
  isModalOpen: boolean;
  toggle: () => void;
  customerProfilData?: CustomerProfileResponse;
  mutate: UseMutateFunction<
    CustomerProfileResponse,
    AxiosError<any>,
    any,
    unknown
  >;
}

const CustomerMaintenanceModal: FC<CustomerMaintenanceModalProps> = ({
  isModalOpen,
  toggle,
  customerProfilData,
  mutate,
}) => {
  const isMobile: boolean = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
  );

  const customerProfileId = getItem("customerProfileId");
  const { profile } = useRecoilValue(isLoggedInSelector);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({ resolver: yupResolver(customerMaintenanceSchema) });

  const {
    data: lastMaintenanceData,
    mutate: lastMaintenanceMutate,
    isLoading,
  } = useCustomerLastMaintenanceMutation();
  const { data: customerMaintenanceData, mutate: customerMaintenanceMutate } =
    useCustomerMaintenanceMutation();
  const { data: devicePartListData, mutate: devicePartListMutation } =
    useDevicePartListMutation();
  const {
    data: staffListData,
    mutate: staffListMutation,
    isLoading: staffListLoading,
  } = useStaffListMutation();

  const [selectedDevice, setSelectedDevice] = useState<any>(
    customerProfilData?.customer_devices[0]?.id
  );
  const [selectedFilter, setSelectedFilter] = useState<any>("0");
  const [lastMaintenanceList, setLastMaintenanceList] = useState<
    CustomerLastMaintenanceResponse | undefined
  >();

  const onSubmit: SubmitHandler<any> = (requestFormValues: any) => {
    if (customerProfilData?.customer_devices[0]?.device?.id) {
      customerMaintenanceMutate({
        ...requestFormValues,
        customerId: customerProfileId,
        maintenance_date: new Date(),
        customer_device: selectedDevice,
        device_parts: lastMaintenanceList,
      });
    }
  };

  const updateLastMaintenanceList = (
    indexLayout: number,
    newOperationType: string
  ) => {
    setLastMaintenanceList((prevData: any) =>
      prevData.map((item: any, index: number) =>
        index === indexLayout
          ? { ...item, operation_type: newOperationType }
          : item
      )
    );
  };

  const deleteItem = (index: number) => {
    setLastMaintenanceList(
      (prevData) =>
        prevData?.filter(
          (item, idx) =>
            idx !== index || item?.operation_type === "no_operation"
        )
    );
  };

  useEffect(() => {
    lastMaintenanceMutate({ id: customerProfileId });
    devicePartListMutation({});
    staffListMutation({});
  }, []);

  useEffect(() => {
    const changedIdLastMaintenanceData = lastMaintenanceData?.map(
      (item: any) => {
        return {
          ...item,
          device_part: {
            ...item.device_part,
            name: item.device_part.name,
            description: item.device_part.description,
          },
          device_part_id: item.device_part.id,
          operation_type: OperationTypes.NO_OPERATION,
        };
      }
    );

    setLastMaintenanceList(changedIdLastMaintenanceData);
  }, [lastMaintenanceData]);

  useEffect(() => {
    if (customerMaintenanceData !== undefined) {
      mutate({ id: customerProfileId });
      toggle();
    }
  }, [customerMaintenanceData]);

  const staffListOptions = staffListData?.map((staff) => {
    return {
      key: staff?.fullname,
      value: staff?.id.toString(),
    };
  });

  const customerDeviceOptions = customerProfilData?.customer_devices?.map(
    (customerDevice) => {
      return {
        key: customerDevice?.device?.name,
        value: customerDevice?.device?.id.toString(),
      };
    }
  );

  const getFormattedDevicePartResponse = devicePartListData?.map(
    (devicePart) => {
      return { key: devicePart?.name, value: devicePart?.id };
    }
  );

  const selectedFilterById = devicePartListData?.find(
    (devicePartList) => devicePartList?.id == selectedFilter
  );

  return (
    <ModalComponent
      header="Yeni Bakım Ekle"
      description="Yeni Bakım eklemek için gerekli alanları doldurunuz."
      isOpen={isModalOpen}
      toggle={toggle}
      scrollable
    >
      <Form onSubmit={handleSubmit(onSubmit)} className="custom-form mt-4 pt-2">
        <Row>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label className="form-label">Cihaz Modeli</Label>
              {customerDeviceOptions && (
                <SelectBox
                  onChangeHandler={(e) => setSelectedDevice(e.target.value)}
                  control={control}
                  name="device"
                  className="form-control"
                  defaultValue={customerDeviceOptions[0].value}
                  options={customerDeviceOptions}
                />
              )}
            </FormGroup>
          </Col>

          <>
            <Col sm="12" md="6" lg="6">
              <FormGroup className="mb-3">
                <Label htmlFor="validationCustom02">Yapılan İşlem</Label>
                <TextField
                  control={control}
                  name="procedure"
                  className="form-control"
                  type="text"
                  placeholder="Yapılan İşlem"
                  error={errors.procedure?.message}
                />
              </FormGroup>
            </Col>

            <Col sm="12" md="6" lg="6">
              <FormGroup className="mb-3">
                <Label htmlFor="validationCustom02">Not</Label>
                <TextField
                  control={control}
                  name="note"
                  className="form-control"
                  type="text"
                  placeholder="Not"
                />
              </FormGroup>
            </Col>

            <Col sm="12" md="6" lg="6">
              <FormGroup className="mb-3">
                <Label htmlFor="validationCustom02">İleri Bakım Tarihi</Label>
                <DatePicker
                  control={control}
                  name="next_maintenance_date"
                  className="form-control"
                  type="text"
                  defaultValue={new Date()}
                  placeholder="Not"
                  error={errors.next_maintenance_date?.message}
                />
              </FormGroup>
            </Col>
          </>

          <Col sm="12" md="12" lg="12">
            <ul style={{ padding: "2" }} className="list-unstyled  mb-4">
              {lastMaintenanceList &&
                lastMaintenanceList?.map((lastMaintenance, index) => {
                  return (
                    <li
                      style={{
                        borderBottom: "1px solid #7b8198",
                        paddingBottom: 11,
                      }}
                      key={lastMaintenance.device_part.device_part_id}
                    >
                      <div
                        className="d-flex align-items-start"
                        style={isMobile ? { flexDirection: "column" } : {}}
                      >
                        <div className="flex-grow-1 overflow-hidden h-2">
                          <h5 className="text-truncate font-size-14 mt-2">
                            {lastMaintenance.device_part.name}
                          </h5>

                          {isMobile && (
                            <div className="flex-shrink-0 mb-2 mt-1">
                              <div className="font-size-11">
                                {getOperationTypes(
                                  lastMaintenance.operation_type
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="d-flex align-items-start justify-content-end gap-3 mt-2">
                          <span
                            style={
                              lastMaintenance.operation_type !==
                              OperationTypes.NO_OPERATION
                                ? {
                                    cursor: "not-allowed",
                                    opacity: 0.5,
                                    pointerEvents: "none",
                                  }
                                : { cursor: "pointer" }
                            }
                            onClick={() =>
                              updateLastMaintenanceList(
                                index,
                                OperationTypes.CHANGE
                              )
                            }
                            className="badge btn btn-success rounded-pill font-size-10 p-2"
                          >
                            <i className="mdi mdi-border-color noti-icon"></i>{" "}
                            Değiştir
                          </span>
                          <span
                            style={
                              lastMaintenance.operation_type !==
                              OperationTypes.NO_OPERATION
                                ? {
                                    cursor: "not-allowed",
                                    opacity: 0.5,
                                    pointerEvents: "none",
                                  }
                                : { cursor: "pointer" }
                            }
                            onClick={() =>
                              updateLastMaintenanceList(
                                index,
                                OperationTypes.REPAIR
                              )
                            }
                            className="badge btn btn-info rounded-pill font-size-10 p-2"
                          >
                            <i className="mdi mdi-application-settings noti-icon"></i>{" "}
                            Onar
                          </span>
                          <span
                            style={
                              lastMaintenance.operation_type ===
                              OperationTypes.ADD
                                ? {
                                    cursor: "not-allowed",
                                    opacity: 0.5,
                                    pointerEvents: "none",
                                  }
                                : { cursor: "pointer" }
                            }
                            onClick={() =>
                              updateLastMaintenanceList(
                                index,
                                OperationTypes.NO_OPERATION
                              )
                            }
                            className="badge btn btn-secondary rounded-pill font-size-10 p-2 pointer"
                          >
                            <i className="mdi mdi-arrange-send-backward noti-icon"></i>{" "}
                            Geri Al
                          </span>
                          <span
                            onClick={() => {
                              lastMaintenance.operation_type !==
                              OperationTypes.ADD
                                ? updateLastMaintenanceList(
                                    index,
                                    OperationTypes.DELETE
                                  )
                                : deleteItem(index);
                            }}
                            className="badge btn btn-danger rounded-pill font-size-10 p-2 pointer"
                          >
                            <i className="mdi mdi-close noti-icon"></i> Çıkar
                          </span>
                        </div>
                      </div>

                      {!isMobile && (
                        <div className="flex-shrink-0 mb-2 mt-1">
                          <div className="font-size-11">
                            {getOperationTypes(lastMaintenance.operation_type)}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
            </ul>
          </Col>

          <Col sm="12" md="12" lg="12">
            <FormGroup className="mb-3">
              <Label className="form-label">Malzeme Ekle</Label>
              <Card className={"mb-xl-0"}>
                <CardBody>
                  <Row>
                    <Col sm="12" md="6" lg="6">
                      {getFormattedDevicePartResponse && (
                        <SelectBox
                          name="devicePart"
                          className="form-control"
                          defaultValue="0"
                          onChangeHandler={(e) =>
                            setSelectedFilter(e.target.value)
                          }
                          options={[
                            ...getFormattedDevicePartResponse,
                            { key: "Lütfen malzeme seçin", value: "0" },
                          ]}
                        />
                      )}
                    </Col>

                    <Col
                      className={isMobile ? "mt-3" : ""}
                      sm="12"
                      md="6"
                      lg="6"
                    >
                      <Link
                        onClick={() => {
                          if (selectedFilter.length && selectedFilter !== "0") {
                            setLastMaintenanceList((prevData: any) => [
                              ...prevData,
                              {
                                device_part: {
                                  name: selectedFilterById?.name,
                                  description: selectedFilterById?.description,
                                },
                                operation_type: OperationTypes.ADD,
                                device_part_id: selectedFilterById?.id,
                              },
                            ]);
                          }
                        }}
                        to="#"
                        className={"btn btn-success w-100"}
                      >
                        Malzeme Ekle
                      </Link>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </FormGroup>
          </Col>

          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label className="form-label">Bakımı Yapan</Label>
              {staffListOptions && (
                <SelectBox
                  control={control}
                  name="person_who_maintain"
                  defaultValue={profile?.id}
                  className="form-control"
                  options={staffListOptions}
                />
              )}
            </FormGroup>
          </Col>

          <Col md="12">
            <FormGroup className="mb-3">
              <Button loading={isLoading} text="Bakım Yap" variant="primary" />
            </FormGroup>
          </Col>
        </Row>
      </Form>
    </ModalComponent>
  );
};

export default CustomerMaintenanceModal;
